import React, { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import i18next from "src/i18n/config";
import TimeService from "src/services/timeService/timeService";
import { selectCurrentWorkspaceId } from "src/store/app";
import {
    useCancelClientSubscriptionMutation,
    useReviveClientSubscriptionMutation,
    useUpdateClientExpirationMutation,
    useUpdateClientMembersMutation,
    useUpdateClientWorkspaceMutation,
} from "src/ui/containers/clients/api";
import { ResellerClient, resellerDescriptionMaxLength } from "src/ui/containers/clients/types";

import { ConfirmDialog } from "@dashboard/devices/components/shared/ConfirmDialog";
import { AddOwnerDialog } from "@dashboard/shared/components/AddOwnerDialog";
import { SubscriptionStatusLabel } from "@dashboard/shared/subscription/SubscriptionStatusLabel";
import { Box, DialogContentText, Typography, useTheme } from "@mui/material";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { MinimalTable, MinimalTableCell, MinimalTableHeader, MinimalTableRow } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { TableHeaderRows } from "@shared/table/TableHeader";
import { TableRowCells } from "@shared/table/TableRowCells";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { CellContext, ColumnDef, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";

import { PaneElement } from "../../shared/PaneElement";
import { PropertyKey, PropertyRow, PropertyValue } from "../../shared/Property";

import { useStyles } from "./WorkspaceTab.styles";

type OwnerData = {
    id: number;
    email: string;
    role: string;
};

const EmailCell = (props: CellContext<OwnerData, unknown>) => {
    const { email } = props.row.original;

    return (
        <MinimalTableCell align="justify">
            <Typography>{email}</Typography>
        </MinimalTableCell>
    );
};

type ActionCellProps = CellContext<OwnerData, unknown> & {
    workspaceId: number | null;
    workspaceName: string;
    clientId: number;
};

const ActionCell = ({ row, workspaceId, workspaceName, clientId }: ActionCellProps) => {
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [updateClientMembers] = useUpdateClientMembersMutation();
    const { t } = useTranslation(["common", "modals", "sidepanels"]);
    const { email, role } = row.original;

    const onConfirmDelete = async () => {
        if (workspaceId === null) {
            return;
        }

        try {
            await updateClientMembers({
                workspaceId,
                clientId,
                removedMembers: [email],
            }).unwrap();
            showSuccessToast(t("sidepanels:clientsPanel.workspaceTab.toast.ownerRemovedSuccess"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:clientsPanel.workspaceTab.toast.ownerRemovedFailed"));
        } finally {
            setIsDeleteModalOpen(false);
        }
    };

    return (
        <MinimalTableCell align="right">
            <DangerOutlinedButton onClick={() => setIsDeleteModalOpen(true)}>
                <i className="fa-solid fa-trash" />
            </DangerOutlinedButton>
            <ConfirmDialog
                title={t("modals:removeMember.title")}
                dangerButton
                primaryActionText={t("sidepanels:clientsPanel.workspaceTab.button.remove")}
                open={isDeleteModalOpen}
                onConfirm={onConfirmDelete}
                onClose={() => setIsDeleteModalOpen(false)}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="removeMember.descriptionWorkspace"
                        values={{
                            email,
                            role,
                            name: workspaceName,
                        }}
                        components={{
                            bold: <strong />,
                        }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </MinimalTableCell>
    );
};

const createOwnerColumns = (
    workspaceId: number | null,
    workspaceName: string,
    clientId: number,
): ColumnDef<OwnerData, unknown>[] => [
    {
        id: "email",
        header: i18next.t("common:label.email"),
        cell: EmailCell,
        enableSorting: true,
        accessorKey: "email",
        sortingFn: "alphanumeric",
    },
    {
        id: "action",
        header: "",
        cell: (cellProps) => (
            <ActionCell {...cellProps} workspaceId={workspaceId} workspaceName={workspaceName} clientId={clientId} />
        ),
    },
];

type ClientWorkspaceEdit = {
    organizationName?: string;
    resellerDescription?: string;
};

const WorkspaceTab = (props: { workspace: ResellerClient }) => {
    const currentWorkspaceId = useSelector(selectCurrentWorkspaceId);
    const { workspace } = props;
    const { t } = useTranslation(["common", "modals", "sidepanels"]);
    const [cancelSubscriptionDialogOpen, setCancelSubscriptionDialogOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [reviveClientSubscription] = useReviveClientSubscriptionMutation();
    const [cancelClientSubscription] = useCancelClientSubscriptionMutation();
    const [updateClientExpiration] = useUpdateClientExpirationMutation();
    const [updateClientWorkspace] = useUpdateClientWorkspaceMutation();

    const theme = useTheme();
    const { classes } = useStyles();

    const subscription = workspace.subscription;
    const isCancelled = subscription?.status === "Cancelled" || !!subscription?.cancellationDate;

    const handleCancelSubscription = () => {
        setCancelSubscriptionDialogOpen(true);
    };

    const handleReviveSubscription = async () => {
        if (currentWorkspaceId === null) {
            return;
        }

        try {
            await reviveClientSubscription({
                workspaceId: currentWorkspaceId,
                clientId: workspace.id,
            }).unwrap();
            showSuccessToast(t("sidepanels:clientsPanel.workspaceTab.toast.subscriptionRevivedSuccess"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:clientsPanel.workspaceTab.toast.subscriptionRevivedFailed"));
        }
    };

    const confirmCancelSubscription = async () => {
        if (currentWorkspaceId === null) {
            return;
        }

        try {
            const currentDate = new Date().toISOString();
            await cancelClientSubscription({
                workspaceId: currentWorkspaceId,
                clientId: workspace.id,
                date: currentDate,
            }).unwrap();
            showSuccessToast(t("sidepanels:clientsPanel.workspaceTab.toast.subscriptionCancelledSuccess"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:clientsPanel.workspaceTab.toast.subscriptionCancelledFailed"));
        }
    };

    const handleSaveExpirationDate = async ({ dateString }: { dateString: string | null }) => {
        if (dateString && currentWorkspaceId !== null) {
            try {
                await updateClientExpiration({
                    workspaceId: currentWorkspaceId,
                    clientId: workspace.id,
                    date: dateString,
                }).unwrap();
                showSuccessToast(
                    t("sidepanels:clientsPanel.workspaceTab.toast.expirationDateUpdated", {
                        date: dateString,
                    }),
                );
            } catch (_error) {
                const err = _error as Error;
                showErrorToast(err.message || t("sidepanels:clientsPanel.workspaceTab.toast.expirationDateUpdateFailed"));
            }
        }
    };

    /**
     * Sends `name` — which the endpoint documents as required — plus only the field that changed. The backend
     * patches per field, so a narrow body avoids re-validating `organizationUrl` and `defaultGroupId` (which
     * must name a live group, and is 0 when unset); a description edit has no business failing on either. It
     * also keeps us from echoing back a stale copy of fields somebody else may have just changed.
     */
    const saveClientWorkspace = async (edit: ClientWorkspaceEdit) => {
        if (currentWorkspaceId === null) {
            return;
        }

        try {
            await updateClientWorkspace({
                workspaceId: currentWorkspaceId,
                clientId: workspace.id,
                name: workspace.name,
                ...edit,
            }).unwrap();
            showSuccessToast(t("sidepanels:clientsPanel.workspaceTab.toast.updateSuccess"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || t("sidepanels:clientsPanel.workspaceTab.toast.updateFailed"));
        }
    };

    const owners = useMemo(
        () =>
            workspace.members
                ?.filter((member) => member.role === "Owner")
                .map((owner, index) => ({
                    id: index,
                    email: owner.email,
                    role: owner.role,
                })) || [],
        [workspace.members],
    );

    const table = useReactTable({
        data: owners,
        columns: createOwnerColumns(currentWorkspaceId, workspace.name, workspace.id),
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <PaneElement.Container>
            <PaneElement.Section>
                <PaneElement.Header>{t("sidepanels:clientsPanel.workspaceTab.subscriptionDetails")}</PaneElement.Header>

                <PropertyRow className={classes.row}>
                    <PropertyKey>{t("common:label.organizationName")}</PropertyKey>
                    <PropertyValue style={{ display: "block" }}>
                        <PaneElement.EditableInput
                            initialValue={workspace?.organizationName ?? ""}
                            onSave={(value) => saveClientWorkspace({ organizationName: value })}
                        />
                    </PropertyValue>
                </PropertyRow>

                <PropertyRow className={classes.row}>
                    <PropertyKey>{t("common:label.description")}</PropertyKey>
                    <PropertyValue style={{ display: "block" }}>
                        <PaneElement.EditableInput
                            initialValue={workspace?.resellerDescription ?? ""}
                            onSave={(value) => saveClientWorkspace({ resellerDescription: value })}
                            multiline
                            minRows={3}
                            maxLength={resellerDescriptionMaxLength}
                        />
                    </PropertyValue>
                </PropertyRow>

                <PropertyRow className={classes.row}>
                    <PropertyKey>{t("sidepanels:clientsPanel.workspaceTab.status")}</PropertyKey>
                    <PropertyValue>
                        {subscription?.status ? <SubscriptionStatusLabel status={subscription.status} /> : "-"}
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.row}>
                    <PropertyKey>{t("sidepanels:clientsPanel.workspaceTab.chargeId")}</PropertyKey>
                    <PropertyValue>
                        <Typography variant="body1">{subscription?.chargeId || "-"}</Typography>
                    </PropertyValue>
                </PropertyRow>
                <PropertyRow className={classes.row}>
                    <PropertyKey>{t("common:label.expirationDate")}</PropertyKey>
                    <PropertyValue style={{ display: "block" }}>
                        <PaneElement.EditableDateInput
                            initialValue={subscription?.expirationDate}
                            onSave={handleSaveExpirationDate}
                            placeholder={t("sidepanels:clientsPanel.workspaceTab.selectExpirationDate")}
                        />
                    </PropertyValue>
                </PropertyRow>

                {subscription?.cancellationDate && (
                    <PropertyRow className={classes.row}>
                        <PropertyKey>{t("sidepanels:clientsPanel.workspaceTab.cancellationDate")}</PropertyKey>
                        <PropertyValue>
                            <Typography variant="body1">
                                {TimeService.getLocalDate(subscription.cancellationDate, "ISO", "date")}
                            </Typography>
                        </PropertyValue>
                    </PropertyRow>
                )}
            </PaneElement.Section>

            <PaneElement.Section>
                {isCancelled ? (
                    <React.Fragment>
                        <PaneElement.Header>{t("sidepanels:clientsPanel.workspaceTab.reviveSubscription")}</PaneElement.Header>
                        <Typography className={classes.paragraph}>
                            {t("sidepanels:clientsPanel.workspaceTab.reviveSubscriptionDescription")}
                        </Typography>
                        <Box className={classes.subscriptionButton}>
                            <SecondaryButton onClick={handleReviveSubscription} startIcon={<i className="fa-solid fa-play" />}>
                                {t("sidepanels:clientsPanel.workspaceTab.reviveSubscription")}
                            </SecondaryButton>
                        </Box>
                    </React.Fragment>
                ) : (
                    <React.Fragment>
                        <PaneElement.Header>{t("sidepanels:clientsPanel.workspaceTab.cancelSubscription")}</PaneElement.Header>
                        <Typography className={classes.paragraph}>
                            {t("sidepanels:clientsPanel.workspaceTab.cancelSubscriptionDescription")}
                        </Typography>
                        <Box className={classes.subscriptionButton}>
                            <DangerOutlinedButton
                                onClick={handleCancelSubscription}
                                startIcon={<i className="fa-solid fa-ban" />}
                            >
                                {t("sidepanels:clientsPanel.workspaceTab.cancelSubscription")}
                            </DangerOutlinedButton>
                        </Box>
                    </React.Fragment>
                )}
            </PaneElement.Section>

            <PaneElement.Section withoutBorder>
                <Box className={classes.ownersHeader}>
                    <PaneElement.Header>{t("sidepanels:clientsPanel.workspaceTab.workspaceOwners")}</PaneElement.Header>
                    <Box className={classes.ownersActions}>
                        <SecondaryButton onClick={() => setDialogOpen(true)} style={{ marginLeft: theme.spacing(2) }}>
                            <i className="fa-solid fa-user-plus" />
                        </SecondaryButton>
                    </Box>
                </Box>
                <PaneElement.TableContainer>
                    <MinimalTable stickyHeader>
                        <MinimalTableHeader>
                            <TableHeaderRows headerGroups={table.getHeaderGroups()} />
                        </MinimalTableHeader>
                        <TableBody table={table} noDataMessage={t("sidepanels:clientsPanel.workspaceTab.noOwnersFound")}>
                            {table.getRowModel().rows.map((row) => (
                                <MinimalTableRow key={row.id}>
                                    <TableRowCells row={row} />
                                </MinimalTableRow>
                            ))}
                        </TableBody>
                    </MinimalTable>
                </PaneElement.TableContainer>
            </PaneElement.Section>

            <AddOwnerDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                workspaceId={currentWorkspaceId}
                clientId={workspace.id}
            />

            <ConfirmDialog
                title={t("modals:cancelSubscription.title")}
                open={cancelSubscriptionDialogOpen}
                onConfirm={confirmCancelSubscription}
                onClose={() => setCancelSubscriptionDialogOpen(false)}
                dangerButton
                primaryActionText={t("modals:cancelSubscription.button.cancelSubscription")}
                secondaryActionText={t("modals:cancelSubscription.button.keep")}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="cancelClientSubscription.description"
                        values={{ name: workspace.name }}
                        components={{ bold: <strong /> }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </PaneElement.Container>
    );
};

export default WorkspaceTab;
